import React, { useEffect, useState, useCallback, useRef } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useSocket, useSocketEvent } from '../context/SocketContext';
import { StatusBadge } from '../components/Badges';
import PatientDetailModal from '../components/PatientDetailModal';
import ConfirmDialog from '../components/ConfirmDialog';
import NowServingDisplay from '../components/NowServingDisplay';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { Users, Clock, CheckCircle2, IndianRupee, Upload, FileText, Image as ImageIcon, Trash2, Plus, X, Wifi, WifiOff } from 'lucide-react';
import { todayISO } from '../utils/clinicDate';

export default function DoctorDashboard() {
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [dashboardItems, setDashboardItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [deleteItemTarget, setDeleteItemTarget] = useState(null);
  const { showToast } = useToast();
  const { user } = useAuth();
  const [showStats, setShowStats] = React.useState(() => localStorage.getItem('dashboard_showStats') !== 'false');
  const toggleStats = () => setShowStats(v => { localStorage.setItem('dashboard_showStats', !v); return !v; });
  const { connected } = useSocket();

  // Expense/Profit — admin only, each with its own independent hide/show
  const [expenseSummary, setExpenseSummary] = useState(null); // { trend, today, total7Days }
  const [todayExpenses, setTodayExpenses] = useState([]);
  const [showExpenseChart, setShowExpenseChart] = React.useState(() => localStorage.getItem('dashboard_showExpenseChart') !== 'false');
  const [showProfitChart, setShowProfitChart]   = React.useState(() => localStorage.getItem('dashboard_showProfitChart') !== 'false');
  const toggleExpenseChart = () => setShowExpenseChart(v => { localStorage.setItem('dashboard_showExpenseChart', !v); return !v; });
  const toggleProfitChart  = () => setShowProfitChart(v => { localStorage.setItem('dashboard_showProfitChart', !v); return !v; });

  const loadAll = useCallback(async () => {
    try {
      const [statsRes, queueRes, itemsRes, expSummaryRes, todayExpRes] = await Promise.all([
        api.get('/patients/stats'),
        api.get('/patients/queue'),
        api.get('/dashboard/items'),
        api.get('/expenses/summary'),
        api.get('/expenses', { params: { date: todayISO() } }),
      ]);
      setStats(statsRes.data);
      setQueue(queueRes.data.patients);
      setDashboardItems(itemsRes.data.items);
      setExpenseSummary(expSummaryRes.data);
      setTodayExpenses(todayExpRes.data.expenses || []);
    } catch (err) {
      showToast('Could not load dashboard data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadAll();
    // Safety-net poll - real-time socket events below handle the normal
    // case instantly; this just guards against a dropped connection.
    const interval = setInterval(loadAll, 60000);
    return () => clearInterval(interval);
  }, [loadAll]);

  // Real-time: any token created, status changed, or patient archived by
  // either the doctor or receptionist - in this tab or any other - updates
  // this dashboard immediately, including after a daily reset.
  useSocketEvent('queue:updated', () => loadAll());
  useSocketEvent('queue:reset', () => loadAll());
  useSocketEvent('expense:created', () => loadAll());
  useSocketEvent('expense:deleted', () => loadAll());
  useSocketEvent('dashboard:item-added', (payload) => {
    if (payload?.item) setDashboardItems((prev) => [payload.item, ...prev.filter((i) => i._id !== payload.item._id)]);
  });
  useSocketEvent('dashboard:item-deleted', (payload) => {
    if (payload?.id) setDashboardItems((prev) => prev.filter((i) => i._id !== payload.id));
  });

  function handlePatientUpdated(updated) {
    setQueue((prev) => prev.map((p) => (p._id === updated._id ? updated : p)));
  }

  async function confirmDeleteItem() {
    if (!deleteItemTarget) return;
    try {
      await api.delete(`/dashboard/items/${deleteItemTarget._id}`);
      showToast('Dashboard item deleted.', 'success');
      setDashboardItems((prev) => prev.filter((i) => i._id !== deleteItemTarget._id));
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not delete item.', 'error');
    } finally {
      setDeleteItemTarget(null);
    }
  }

  const waiting = queue.filter((p) => p.status === 'waiting');

  return (
    <AppLayout title="Doctor Dashboard" subtitle="Live overview of today's clinic activity">
      <div
        title={connected ? 'Live updates connected' : 'Live updates disconnected — reconnecting...'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          fontWeight: 600,
          color: connected ? 'var(--color-success)' : 'var(--color-warning)',
          marginBottom: 16,
        }}
      >
        {connected ? <Wifi size={15} /> : <WifiOff size={15} />}
        {connected ? 'Live — updates appear instantly' : 'Reconnecting to live updates...'}
      </div>

      <div style={{ marginBottom: 20 }}>
        <NowServingDisplay showNextButton onAdvanced={() => loadAll()} />
      </div>

      {user?.isAdmin && (
        <button className="btn btn-outline btn-sm" onClick={toggleStats}
          style={{ marginBottom:8, display:'flex', alignItems:'center', gap:6, width:'fit-content' }}>
          {showStats ? '▲ Hide Stats' : '▼ Show Stats'}
        </button>
      )}
      {(showStats || !user?.isAdmin) && (
      <div className="stat-grid">
        <StatCard
          icon={Users}
          color="primary"
          label="Today's Patients (total)"
          value={loading ? '—' : stats?.todayTotal ?? 0}
        />
        <StatCard
          icon={Clock}
          color="warning"
          label="Waiting"
          value={loading ? '—' : stats?.statusCounts?.waiting ?? 0}
        />
        <StatCard icon={CheckCircle2} color="success" label="Completed" value={loading ? '—' : stats?.statusCounts?.completed ?? 0} />
        <StatCard icon={IndianRupee} color="info"
          label="Today's Revenue"
          value={loading ? '—' : `₹${(stats?.todayRevenue ?? 0).toLocaleString('en-IN')}`} />
        {user?.isAdmin && (
          <StatCard icon={IndianRupee} color="primary"
            label="Revenue (7 days)"
            value={loading ? '—' : `₹${(stats?.totalRevenue7Days ?? 0).toLocaleString('en-IN')}`} />
        )}
      </div>
      )}


            {!loading && stats?.todayArchived > 0 && (
        <div
          className="card"
          style={{
            padding: '10px 16px',
            marginBottom: 20,
            fontSize: 13,
            color: 'var(--color-ink-soft)',
            background: 'var(--color-info-soft)',
            border: 'none',
          }}
        >
          {stats.todayArchived} of today's {stats.todayTotal} patient{stats.todayTotal === 1 ? '' : 's'} {stats.todayArchived === 1 ? 'has' : 'have'} been cleared from the active queue (e.g. via Reset Daily Tokens). {stats.todayActive} still active. All records remain in "All Patients."
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 12px' }}>Next in Queue</h3>
        {waiting.length === 0 ? (
          <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--color-ink-faint)', fontSize: 13 }}>
            No one waiting right now.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
            {waiting
              .slice()
              .sort((a, b) => (a.tokenNumber || 0) - (b.tokenNumber || 0))
              .slice(0, 6)
              .map((p) => (
                <div key={p._id} className="card" style={{ padding: 16, cursor: 'pointer' }} onClick={() => setSelectedPatient(p)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{p.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--color-ink-faint)', fontFamily: 'var(--font-mono)' }}>
                        Token #{p.tokenNumber} · {p.patientUID}
                      </div>
                    </div>
                    <StatusBadge status={p.status} />
                  </div>
                  {p.appointmentSlot && <div style={{ fontSize: 12, color: 'var(--color-primary)', fontWeight: 600, marginTop: 8 }}>{p.appointmentSlot}</div>}
                  {p.reasonForVisit && <div style={{ fontSize: 12, color: 'var(--color-ink-soft)', marginTop: 4 }}>{p.reasonForVisit}</div>}
                </div>
              ))}
          </div>
        )}
        {waiting.length > 6 && (
          <p style={{ fontSize: 13, color: 'var(--color-ink-faint)', marginTop: 10 }}>
            {waiting.length - 6} more patient{waiting.length - 6 !== 1 ? 's' : ''} waiting in queue.
          </p>
        )}
      </div>

      {user?.isAdmin && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 12px' }}>Expenses</h3>

          {/* Today's expense details */}
          <div className="card" style={{ padding: '14px 16px', marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-ink-faint)', textTransform: 'uppercase', marginBottom: 10 }}>
              Today's Expenses ({todayExpenses.length}) — Total ₹{(todayExpenses.reduce((s,e)=>s+(e.amount||0),0)).toLocaleString('en-IN')}
            </div>
            {todayExpenses.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--color-ink-faint)', fontStyle: 'italic' }}>No expenses recorded today.</div>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {todayExpenses.map(e => (
                  <div key={e._id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}>
                    {e.category === 'doctor_cost' && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 100, background: 'var(--color-warning-soft)', color: 'var(--color-warning)', flexShrink: 0 }}>
                        Doctor Cost
                      </span>
                    )}
                    <span style={{ flex: 1 }}>
                      {e.note}
                      {e.patientUID && <span style={{ color: 'var(--color-ink-faint)' }}> · {e.patientUID}</span>}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--color-ink-faint)' }}>
                      {e.method}{e.addedBy ? ` · by ${e.addedBy}` : ''}
                      {e.createdAt ? ` · ${new Date(e.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}` : ''}
                    </span>
                    <span style={{ fontWeight: 700, color: 'var(--color-danger)' }}>₹{(e.amount||0).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 16 }}>
            {/* Expense chart */}
            <div className="card lift3d-card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showExpenseChart ? 16 : 0 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Expenses — Last 7 Days</h3>
                <button className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '3px 10px' }} onClick={toggleExpenseChart}>
                  {showExpenseChart ? '▲ Hide' : '▼ Show'}
                </button>
              </div>
              {showExpenseChart && expenseSummary?.trend && (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={expenseSummary.trend}>
                    <defs>
                      <linearGradient id="expenseBarGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--color-danger)" stopOpacity={1} />
                        <stop offset="100%" stopColor="var(--color-danger)" stopOpacity={0.55} />
                      </linearGradient>
                      <filter id="barDropShadow" x="-30%" y="-10%" width="160%" height="140%">
                        <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.25" />
                      </filter>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: 'var(--color-ink-faint)' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: 'var(--color-ink-faint)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13 }} />
                    <Bar dataKey="value" fill="url(#expenseBarGradient)" radius={[6, 6, 0, 0]} filter="url(#barDropShadow)" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Profit chart — revenue collected minus expenses, per day */}
            <div className="card lift3d-card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showProfitChart ? 16 : 0 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Profit — Last 7 Days</h3>
                <button className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '3px 10px' }} onClick={toggleProfitChart}>
                  {showProfitChart ? '▲ Hide' : '▼ Show'}
                </button>
              </div>
              {showProfitChart && stats?.revenueTrend && expenseSummary?.trend && (() => {
                const expenseByDate = {};
                expenseSummary.trend.forEach(e => { expenseByDate[e.date] = e.value; });
                const profitData = stats.revenueTrend.map(r => ({
                  label: r.label,
                  value: r.value - (expenseByDate[r.date] || 0),
                }));
                return (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={profitData}>
                      <defs>
                        <linearGradient id="profitBarGradientUp" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="var(--color-success)" stopOpacity={1} />
                          <stop offset="100%" stopColor="var(--color-success)" stopOpacity={0.55} />
                        </linearGradient>
                        <linearGradient id="profitBarGradientDown" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="var(--color-danger)" stopOpacity={1} />
                          <stop offset="100%" stopColor="var(--color-danger)" stopOpacity={0.55} />
                        </linearGradient>
                        <filter id="profitBarDropShadow" x="-30%" y="-10%" width="160%" height="140%">
                          <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.25" />
                        </filter>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis dataKey="label" tick={{ fontSize: 12, fill: 'var(--color-ink-faint)' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 12, fill: 'var(--color-ink-faint)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip contentStyle={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 13 }} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} filter="url(#profitBarDropShadow)">
                        {profitData.map((d, i) => (
                          <Cell key={i} fill={d.value >= 0 ? 'url(#profitBarGradientUp)' : 'url(#profitBarGradientDown)'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Dashboard Files & Reports</h3>
        <button className="btn btn-outline btn-sm" onClick={() => setUploadModalOpen(true)}>
          <Plus size={14} />
          Add File
        </button>
      </div>

      {dashboardItems.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--color-ink-faint)', fontSize: 13 }}>
          No files added yet. Upload images or PDFs (like X-rays or reports) along with optional chart data.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {dashboardItems.map((item, i) => (
            <div key={item._id} className="stagger-in" style={{ '--stagger-i': i }}>
              <DashboardItemCard item={item} onDelete={() => setDeleteItemTarget(item)}
                onLinked={(updated) => setDashboardItems((prev) => prev.map((it) => (it._id === updated._id ? updated : it)))} />
            </div>
          ))}
        </div>
      )}

      {selectedPatient && (
        <PatientDetailModal
          patient={selectedPatient}
          editable
          onClose={() => setSelectedPatient(null)}
          onUpdated={(updated) => {
            handlePatientUpdated(updated);
            setSelectedPatient(updated);
          }}
        />
      )}

      {uploadModalOpen && (
        <UploadDashboardItemModal
          onClose={() => setUploadModalOpen(false)}
          onUploaded={() => {
            // Don't insert the item here — the 'dashboard:item-added' socket
            // event (emitted by the server right after it saves the item)
            // is the ONLY place that adds it to state. Having two separate
            // code paths add the same item (even with matching de-dupe
            // logic in each) is exactly the kind of double-insertion that's
            // easy to get subtly wrong; a single source of truth can't.
            setUploadModalOpen(false);
          }}
        />
      )}

      <ConfirmDialog
        open={!!deleteItemTarget}
        title="Delete this file?"
        message={`"${deleteItemTarget?.title}" will be permanently removed from the dashboard.`}
        confirmLabel="Delete"
        danger
        onConfirm={confirmDeleteItem}
        onCancel={() => setDeleteItemTarget(null)}
      />
    </AppLayout>
  );
}

function StatCard({ icon: Icon, color, label, value }) {
  return (
    <div className="tilt3d-scene">
      <div className="stat-card tilt3d-card">
        <div className="stat-card-icon" style={{ background: `var(--color-${color}-soft)` }}>
          <Icon size={18} color={`var(--color-${color})`} />
        </div>
        <div className="stat-card-label">{label}</div>
        <div className="stat-card-value">{value}</div>
      </div>
    </div>
  );
}

function DashboardItemCard({ item, onDelete, onLinked }) {
  const apiOrigin = (process.env.REACT_APP_API_URL || '').replace(/\/api$/, '');
  const [linking, setLinking] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const { showToast } = useToast();

  useEffect(() => {
    if (!linking || !query.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get('/patients/search', { params: { q: query.trim() } })
        .then(res => setResults(res.data.patients || []))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [query, linking]);

  async function linkPatient(p) {
    try {
      const res = await api.put(`/dashboard/items/${item._id}`, {
        patientId: p._id, patientUID: p.patientUID, patientName: p.name,
      });
      onLinked(res.data.item);
      showToast(`Linked to ${p.name} — now visible in their Patient Tracker.`, 'success');
      setLinking(false); setQuery(''); setResults([]);
    } catch (err) {
      showToast(err.response?.data?.message || 'Could not link patient.', 'error');
    }
  }

  return (
    <div className="card lift3d-card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {item.fileType === 'pdf' ? <FileText size={18} color="var(--color-danger)" /> : <ImageIcon size={18} color="var(--color-info)" />}
          <span style={{ fontWeight: 700, fontSize: 14 }}>{item.title}</span>
        </div>
        <button className="btn btn-icon btn-ghost" onClick={onDelete} aria-label="Delete">
          <Trash2 size={15} color="var(--color-danger)" />
        </button>
      </div>
      {item.description && <p style={{ fontSize: 12, color: 'var(--color-ink-soft)', margin: '0 0 10px' }}>{item.description}</p>}

      {item.fileType === 'image' ? (
        <a href={`${apiOrigin}${item.filePath}`} target="_blank" rel="noopener noreferrer">
          <img src={`${apiOrigin}${item.filePath}`} alt={item.title} style={{ width: '100%', borderRadius: 8, maxHeight: 160, objectFit: 'cover' }} />
        </a>
      ) : (
        <a href={`${apiOrigin}${item.filePath}`} target="_blank" rel="noopener noreferrer" className="btn btn-outline btn-sm" style={{ width: '100%' }}>
          <FileText size={14} />
          Open PDF
        </a>
      )}

      {item.graphData && item.graphData.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <ResponsiveContainer width="100%" height={100}>
            <BarChart data={item.graphData}>
              <defs>
                <linearGradient id={`miniBarGradient-${item._id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--color-primary)" stopOpacity={1} />
                  <stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0.55} />
                </linearGradient>
                <filter id={`miniBarShadow-${item._id}`} x="-30%" y="-10%" width="160%" height="140%">
                  <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.2" />
                </filter>
              </defs>
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--color-ink-faint)' }} axisLine={false} tickLine={false} />
              <Bar dataKey="value" fill={`url(#miniBarGradient-${item._id})`} radius={[4, 4, 0, 0]} filter={`url(#miniBarShadow-${item._id})`} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Patient link — so this file also shows in that patient's Patient
          Tracker history, not only in this general dashboard list. */}
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--color-border)' }}>
        {item.patientUID ? (
          <div style={{ fontSize: 12, color: 'var(--color-ink-soft)' }}>
            🔗 Linked to <strong>{item.patientName}</strong> <span style={{ color: 'var(--color-ink-faint)', fontFamily: 'var(--font-mono)' }}>{item.patientUID}</span>
          </div>
        ) : linking ? (
          <div style={{ position: 'relative' }}>
            <input className="input" style={{ fontSize: 12, padding: '5px 8px' }} autoFocus
              value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search patient by name or ID…" />
            {results.length > 0 && (
              <div className="card" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4, maxHeight: 160, overflowY: 'auto', padding: 4 }}>
                {results.slice(0, 8).map(p => (
                  <div key={p._id} style={{ padding: '6px 8px', cursor: 'pointer', borderRadius: 6, fontSize: 12 }}
                    onMouseDown={(e) => e.preventDefault()} onClick={() => linkPatient(p)}>
                    {p.name} <span style={{ color: 'var(--color-ink-faint)', fontFamily: 'var(--font-mono)' }}>{p.patientUID}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 12, padding: '2px 4px' }} onClick={() => setLinking(true)}>
            + Link to a patient
          </button>
        )}
      </div>
    </div>
  );
}

function UploadDashboardItemModal({ onClose, onUploaded }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [graphRows, setGraphRows] = useState([{ label: '', value: '' }]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { showToast } = useToast();

  // Optional — link this file to a specific patient/visit so it also shows
  // up in that patient's Patient Tracker history, not only in this general
  // dashboard list (which is only ever grouped by upload date).
  const [patientQuery, setPatientQuery] = useState('');
  const [patientResults, setPatientResults] = useState([]);
  const [linkedPatient, setLinkedPatient] = useState(null);
  useEffect(() => {
    if (!patientQuery.trim() || linkedPatient) { setPatientResults([]); return; }
    const t = setTimeout(() => {
      api.get('/patients/search', { params: { q: patientQuery.trim() } })
        .then(res => setPatientResults(res.data.patients || res.data.results || []))
        .catch(() => setPatientResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [patientQuery, linkedPatient]);

  function updateGraphRow(idx, field, value) {
    setGraphRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  }
  function addGraphRow() {
    setGraphRows((rows) => [...rows, { label: '', value: '' }]);
  }
  function removeGraphRow(idx) {
    setGraphRows((rows) => rows.filter((_, i) => i !== idx));
  }

  async function handleUpload() {
    if (!title.trim()) {
      showToast('Please enter a title.', 'error');
      return;
    }
    if (!file) {
      showToast('Please choose an image or PDF file.', 'error');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('description', description);
    const validGraphData = graphRows
      .filter((r) => r.label.trim() && r.value !== '')
      .map((r) => ({ label: r.label, value: Number(r.value) }));
    if (validGraphData.length > 0) {
      formData.append('graphData', JSON.stringify(validGraphData));
    }
    if (linkedPatient) {
      formData.append('patientId', linkedPatient._id);
      formData.append('patientUID', linkedPatient.patientUID);
      formData.append('patientName', linkedPatient.name);
    }

    try {
      const res = await api.post('/dashboard/items', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast('File added to dashboard.', 'success');
      onUploaded(res.data.item);
    } catch (err) {
      showToast(err.response?.data?.message || 'Upload failed.', 'error');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 22px 0' }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Add Dashboard File</h2>
          <button className="btn btn-icon btn-ghost" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: 22 }}>
          <div className="field">
            <label className="label">Title *</label>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Weekly Patient Report" />
          </div>
          <div className="field">
            <label className="label">Description</label>
            <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional short note" />
          </div>
          <div className="field">
            <label className="label">File (Image or PDF) *</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf"
              className="input"
              onChange={(e) => setFile(e.target.files[0])}
            />
          </div>

          <div className="field">
            <label className="label">Link to a patient (optional)</label>
            {linkedPatient ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--color-surface)', borderRadius: 8, fontSize: 13 }}>
                <span style={{ flex: 1 }}>{linkedPatient.name} <span style={{ color: 'var(--color-ink-faint)', fontFamily: 'var(--font-mono)' }}>{linkedPatient.patientUID}</span></span>
                <button type="button" className="btn btn-outline btn-sm" style={{ padding: '2px 8px', fontSize: 11 }} onClick={() => { setLinkedPatient(null); setPatientQuery(''); }}>Clear</button>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <input className="input" value={patientQuery} onChange={(e) => setPatientQuery(e.target.value)}
                  placeholder="Search patient by name or ID — so this shows in their Patient Tracker too" />
                {patientResults.length > 0 && (
                  <div className="card" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4, maxHeight: 200, overflowY: 'auto', padding: 4 }}>
                    {patientResults.slice(0, 8).map(p => (
                      <div key={p._id} style={{ padding: '8px 10px', cursor: 'pointer', borderRadius: 6, fontSize: 13 }}
                        onClick={() => { setLinkedPatient(p); setPatientResults([]); }}
                        onMouseDown={(e) => e.preventDefault()}>
                        {p.name} <span style={{ color: 'var(--color-ink-faint)', fontFamily: 'var(--font-mono)' }}>{p.patientUID}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="field">
            <label className="label">Graph Data (optional)</label>
            <p style={{ fontSize: 12, color: 'var(--color-ink-faint)', margin: '0 0 8px' }}>
              Add labeled values to show as a small chart on this card, e.g. "Mon" / 12
            </p>
            {graphRows.map((row, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input
                  className="input"
                  placeholder="Label"
                  value={row.label}
                  onChange={(e) => updateGraphRow(idx, 'label', e.target.value)}
                />
                <input
                  className="input"
                  type="number"
                  placeholder="Value"
                  value={row.value}
                  onChange={(e) => updateGraphRow(idx, 'value', e.target.value)}
                />
                <button className="btn btn-icon btn-ghost" onClick={() => removeGraphRow(idx)}>
                  <X size={14} />
                </button>
              </div>
            ))}
            <button className="btn btn-ghost btn-sm" onClick={addGraphRow}>
              <Plus size={14} />
              Add row
            </button>
          </div>

          <button className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} onClick={handleUpload} disabled={uploading}>
            <Upload size={16} />
            {uploading ? 'Uploading...' : 'Upload to Dashboard'}
          </button>
        </div>
      </div>
    </div>
  );
}
